class MobileDock extends HTMLElement {
  constructor() {
    super();
    this.ticking = false;
    this.cachedHeight = null;
  }

  connectedCallback() {
    // Cache header position once on load - avoid repeated getBoundingClientRect
    const header = document.querySelector('.shopify-section-header');
    this.scrollY = header ? Math.round(header.getBoundingClientRect().bottom) : 0;

    this.lastScrollY = 0;
    this.footerBuffer = 100;
    this.isAction = false;

    if (Shopify && Shopify.designMode) {
      this.scrollY = 0;
    }

    this.querySelectorAll('.dock__item[data-action]').forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    });

    const homeLink = this.querySelector('.dock__item[data-home-link]');
    if (homeLink) {
      homeLink.addEventListener('click', this.onHomeClick.bind(this));
    }
    document.addEventListener('menudrawer:close', this.hideStickyHeader.bind(this));
    document.addEventListener('searchmodal:close', this.hideStickyHeader.bind(this));

    // Use passive listener for better scroll performance. Customizer pages
    // hide the dock (theme.liquid), so there is nothing to track there, and
    // the per-frame scrollHeight read forced a layout mid-scroll.
    this.onScrollHandler = this.onScroll.bind(this);
    if (!document.body.classList.contains('is-customizer')) {
      window.addEventListener('scroll', this.onScrollHandler, { passive: true });
    }

    // Initial call with RAF to batch with other layout reads
    requestAnimationFrame(() => {
      this.onScrollHandler();
      this.updateDockHeight();
    });

    // Debounce resize handler
    let resizeTimeout;
    document.addEventListener('matchSmall', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => this.updateDockHeight(), 100);
    });
  }

  updateDockHeight() {
    // Batch the read and write in RAF
    requestAnimationFrame(() => {
      this.cachedHeight = this.offsetHeight;
      document.documentElement.style.setProperty('--mobile-dock-height', `${this.cachedHeight}px`);
    });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  onScroll() {
    // Use requestAnimationFrame to batch DOM reads/writes
    if (!this.ticking) {
      requestAnimationFrame(() => {
        this.updateScrollState();
        this.ticking = false;
      });
      this.ticking = true;
    }
  }

  updateScrollState() {
    const scrollTop = window.pageYOffset;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;

    // Batch class changes
    const shouldBeActive = scrollTop >= this.scrollY;
    const distanceFromBottom = documentHeight - (scrollTop + windowHeight);
    const shouldBeAtFooter = shouldBeActive && distanceFromBottom <= this.footerBuffer;

    // Only modify DOM if state changed
    const isActive = this.classList.contains('is-active');
    const isAtFooter = this.classList.contains('at-footer');

    if (shouldBeActive !== isActive) {
      this.classList.toggle('is-active', shouldBeActive);
    }
    if (shouldBeAtFooter !== isAtFooter) {
      this.classList.toggle('at-footer', shouldBeAtFooter);
    }

    this.lastScrollY = scrollTop;
  }

  onButtonClick(event) {
    event.preventDefault();
    const target = event.currentTarget;
    const delay = this.waitStickyHeader();

    switch (target.dataset.action) {
      case 'cart':
        this.isAction = true;

        const miniCart = document.querySelector('mini-cart');
        if (miniCart) {
          setTimeout(() => {
            miniCart.open();
            document.activeElement.blur();
          }, delay);
        }
        else {
          window.location.href = theme.routes.cart_url;
        }
        break;

      case 'menu':
        this.isAction = true;

        const headerDrawer = document.querySelector('header-drawer');
        if (headerDrawer) {
          setTimeout(() => {
            headerDrawer.openMenuDrawer();
          }, delay);
        }
        break;

      case 'create':
        this.isAction = true;

        const createModal = document.getElementById('create-map-modal');
        if (createModal) {
          setTimeout(() => {
            // Defer to the modal's own opener (exposed in sections/header.liquid)
            // rather than repeating it here. The old inline copy set the height
            // var and display but missed the create-map-modal-open body class,
            // the background header's `inert`, and the resize listener — so a
            // dock-opened modal behaved differently from a header-opened one.
            if (typeof createModal.openCreateModal === 'function') {
              createModal.openCreateModal();
              return;
            }

            // Fallback for the window between DOMContentLoaded and this script
            // wiring up. Deliberately does NOT scroll to top: the modal is
            // position:fixed inset:0, so it covers the viewport from wherever
            // the page is scrolled, and jumping lost the reader's place.
            createModal.style.setProperty('--modal-height', `${window.innerHeight}px`);
            createModal.style.display = 'flex';
            document.body.style.overflow = 'hidden';
          }, delay);
        }
        else {
          // Fallback to starmap product page
          window.location.href = '/products/starmap';
        }
        break;
    }
  }

  onHomeClick(event) {
    // Already on the homepage: scroll back to the top instead of reloading it.
    // Any other page keeps the plain link navigation (and so does a JS failure,
    // since preventDefault only runs on the same-page branch).
    const link = event.currentTarget;
    const normalise = (path) => (path.length > 1 ? path.replace(/\/+$/, '') : path);
    const target = new URL(link.getAttribute('href'), window.location.origin);
    if (normalise(target.pathname) !== normalise(window.location.pathname)) return;

    event.preventDefault();
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  hideStickyHeader() {
    const header = document.querySelector('sticky-header');
    if (header === null) return;

    if (theme.config.mqlSmall && this.isAction && header.sticky()) {
      setTimeout(() => {
        header.hide();
        this.isAction = false;
      }, 500);
    }
  }

  waitStickyHeader() {
    const header = document.querySelector('sticky-header');
    if (header === null) return 0;

    if (!header.sticky() && !this.isElementVisible(header)) {
      header.hide();
      setTimeout(() => header.reveal());
      return 250;
    }

    return 0;
  }

  isElementVisible(element) {
    // Simplified visibility check - less reflow-inducing
    const rect = element.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight;
  }
}
customElements.define('mobile-dock', MobileDock);
